import CONSTANTS from "../api/constants";
import AuthHandler from '../api/authenticationHandler';
import CallManagerHandler from "../api/callManagerHandler";
import { hubConnection, signalR } from 'signalr-no-jquery-tw';
import AgentTeam from "../../stores/models/Agents/AgentTeam";

var connection;
var stopped = false;
var reconnectTimerId = null;
var stopTimer = null;

const agentTeamChanges = (agentTeams, newTeams, teamStore) => {
    let addedTeams = newTeams.filter(newTeam =>
        !agentTeams.find(agentTeam => newTeam.id === agentTeam.team.id)
    );

    let changedNameTeams = newTeams.filter(newTeam =>
        agentTeams.find(agentTeam => (
            newTeam.id === agentTeam.team.id &&
            newTeam.name !== agentTeam.team.name
        ))
    );

    // Find any teams that are no longer in the list
    let removedTeams = agentTeams.filter(agentTeam =>
        !newTeams.find(newTeam =>
            agentTeam.team.id === newTeam.id &&
            teamStore.isValidTeam(agentTeam.team.id)
        )
    );

    return { addedTeams, removedTeams, changedNameTeams };
}

const getAllAgents = (permissionStore) => {
    if (!permissionStore.havePermissions("Call Manager", ["Monitor"])) {
        return Promise.resolve([]);
    }

    return CallManagerHandler.getAllAgents();
};

const removeAgents = (agentDetails, agentStore, currentUser) => {
    let agentDetailsTeamsIds = agentDetails.teams.map((x) => x.id);
    let agentsToRemove = [];

    agentStore.agents.forEach((ag) => {
        // Never remove the currentAgent - this can happen if current agent is not a member of any teams !
        if (ag.id !== currentUser.agent.id) {
            let agentTeamIds = ag.teams.map((t) => t.team.id);
            // if none of this agents teams are in agentDetailsTeamsIds, remove that agent
            if (!agentTeamIds.some((id) => agentDetailsTeamsIds.includes(id))) {
                agentsToRemove.push(ag);
            }
        }
    });
    agentStore.removeAgents(agentsToRemove);
};

const updateAgents = (newAgents, agentStore, currentAgentTeams) => {
    newAgents.forEach((agent) => {
        let existingAgent = agentStore.agents.find((a) => {
            return a.id === agent.id;
        });

        if (existingAgent) {
            // we already have this agent so see if they are part of any of our teams that they don't already have
            let teamsToAdd = agent.teams.filter((t) => {
                return (
                    currentAgentTeams.find((ct) => ct.id === t.id) &&
                    !existingAgent.teams.find((et) => et.team.id === t.id)
                );
            });

            existingAgent.addTeams(teamsToAdd);
        } else {
            // we don't already have this agent so add it
            agent.reasonCode = agent.reasonCode ? agent.reasonCode.code : null;
            agent.statusCode = agent.statusCode ? agent.statusCode.code : null;

            agent.teams = agent.teams.map((t) => {
                return AgentTeam.create({ team: t.id, loggedIn: t.loggedIn });
            });

            agent.currentCallTeam = agent.currentCallTeam
                ? agent.currentCallTeam.id
                : null;

            agentStore.addAgent(agent);
        }
    });
};

const onAgentUpdate = (agentDetails, agentStore, teamStore, currentUser, permissionStore) => {

    agentDetails.statusCode = agentDetails.statusCode ? agentDetails.statusCode.code : null;
    agentDetails.reasonCode = agentDetails.reasonCode ? agentDetails.reasonCode.code : null;

    const agent = agentStore.agents.find((a) => a.id === agentDetails.id);

    if (!agent) {
        // Don't know anything about this agent !
        // Could be that the agent has been added to our team ?
        return;
    }

    const isCurrentAgent = agentDetails.id === currentUser.agent.id;

    // This section of code will sanitize the incoming team data to make it suitable for the data store.
    // This means removing any irrelevant teams from agents, or adding the teams to the agent.
    // It also deals with teams changing name.
    if (isCurrentAgent) {

        // Remove any agents from the agent store which aren't members of any of this agent's current teams
        removeAgents(agentDetails, agentStore, currentUser);

        // Get all agents as there might be agents in teams we have just been added to which don't exist in the store already  
        getAllAgents(permissionStore).then((resp) => {
            // only set the agents if we have permissions
            if (resp.length > 0) {
                updateAgents(resp, agentStore, agentDetails.teams);
            }
        });

        // This is current user
        const teamsChanged = agentTeamChanges(agent.teams, agentDetails.teams, teamStore);

        // Find these teams from the teamstore, and update
        teamsChanged.changedNameTeams.forEach((team) => {
            // Update team from TeamStore with new name
            teamStore.updateTeam(team);
        });

        // Get a list of the Teams in TeamStore having the same reference id as the teams in the agents AgentTeam.
        // Need to do this before removing the AgentTeam from the Agent, otherwise we won't be able to get the data
        let teamStoreTeamsToRemove = teamStore.teams.filter(tsTeam => teamsChanged.removedTeams.find(tc => tsTeam.id === tc.team.id));

        // Iterate all agents, and remove team(s) from them. This includes current agent
        if (teamStoreTeamsToRemove.length > 0) {
            // Remove these AgentTeams from all Agents
            agentStore.removeAgentTeams(teamStoreTeamsToRemove);

            // Remove Team from TeamStore
            teamStore.removeTeams(teamStoreTeamsToRemove);
        }

        teamsChanged.addedTeams.forEach((team) => {
            // Can't iterate all agents and add it in as we don't know which agents are in this team - have to wait for updates from that agent
            // Add team to TeamStore
            teamStore.addTeam(team);
        });
        // Add the teams to the Agent
        agent.addTeams(teamsChanged.addedTeams);
        agent.setCallDetails(agentDetails);
        agent.setAlertTime(agentDetails.alertTime);
        agent.setRegistrationDetails(agentDetails);

        // restart the signalR so we can listen to all users if new teams have been added
        if (teamsChanged.addedTeams.length > 0) {
            if (stopTimer) {
                clearTimeout(stopTimer);
            }
            stopTimer = setTimeout(() => {
                connection.stop();
            }, 100);
        }

    } else {
        // this is another user, remove any non-valid teams before updating rootStore
        agentDetails.teams = agentDetails.teams.filter(agentTeam => teamStore.isValidTeam(agentTeam.id));
        agent.setLocationCode(null);
    }

    // Finally update the agent
    agentStore.updateAgent(agentDetails.id, agentDetails);
}

const onRequestAssistance = (currentUser) => {
    const agent = currentUser.agent;
    if (agent) {
        agent.setAlertTime(new Date().toISOString());
    }
}

const onClearRequestAssistance = (currentUser) => {
    const agent = currentUser.agent;
    if (agent) {
        agent.setAlertTime(null);
    }
}

const onRequestAssistanceAck = (currentUser, message) => {
    const agent = currentUser.agent;
    if (agent && message.id === agent.id) {
        agent.setAlertTime(message.alertTime);
    }
}

const onTelephonyRegistration = (currentUser, message) => {
    const agent = currentUser.agent;
    if (agent) {
        agent.setRegistrationDetails(message);
    }
}

const getDomain = (url) => {
    var hostname;

    //find & remove protocol (http, ftp, etc.) and get hostname
    if (url.indexOf("//") > -1) {
        hostname = url.split('/')[2];
    }
    else {
        hostname = url.split('/')[0];
    }

    //find & remove port number
    hostname = hostname.split(':')[0];
    //find & remove "?"
    hostname = hostname.split('?')[0];

    return hostname;
}

const stop = () => {
    stopped = true;
    if (reconnectTimerId) {
        clearTimeout(reconnectTimerId);
    }
    if (connection) {
        connection.stop();
    }
}

const setAccessToken = (value) => {
    // Set access token on the SignalR connaction so that it is able to authenticate with the platform
    if (connection) {
        connection.accessToken = value;
    }
}

const start = async (agentStore, teamStore, currentUser, permissionStore) => {
    connection = hubConnection(CONSTANTS.API_CONSTANTS.API_BASE_URL + '/signalr');

    stopped = false;

    // get an access token so we can use it in the SignalR connection 
    try {
        let accessToken = await AuthHandler.getToken();
        setAccessToken(accessToken);
    } catch (error) {
        return null;
    }

    const hubProxy = connection.createHubProxy('telephonyEvents');
    if (hubProxy) {
        // Add listeners for events that we're interested in.
        hubProxy.on('onAgentUpdate', function (message) {
            // This update could be for any agent, not just us
            //console.log("SignalR - onAgentUpdate", message);
            onAgentUpdate(message, agentStore, teamStore, currentUser, permissionStore);
        });

        hubProxy.on('onTelephonyRegistration', (message) => {
            // onTelephonyRegistration is only for currently logged in user
            //console.log("SignalR - onTelephonyRegistration", message);
            onTelephonyRegistration(currentUser, message);
        });

        hubProxy.on('onRequestAssistanceAck', (message) => {
            // Supervisor has acknowledged assiatance
            console.warn('onRequestAssistanceAck', message);
            onRequestAssistanceAck(currentUser, message);
        });

        hubProxy.on('onClearRequestAssistance', () => {
            console.warn('onClearRequestAssistance');
            onClearRequestAssistance(currentUser);
        });

        hubProxy.on('onRequestAssistance', () => {
            // User based - user has requested assistance
            console.warn('onRequestAssistance');
            onRequestAssistance(currentUser);
        });
    }

    // add a disconnected event handler
    connection.disconnected(function () {
        // Reconnect if we weren't shutdown by the stop function
        if (!stopped) {
            reconnectTimerId = setTimeout(() => {
                start(agentStore, teamStore, currentUser, permissionStore);
            }, 500);
        }
    });

    connection.error(function (error) {
        // only output error if not disconnected
        if (connection.state !== signalR.connectionState.disconnected) {
            console.log('SignalR Error: ', error);
        }
    });

    // Setup cookie for when WebSockets are used
    let domain = getDomain(CONSTANTS.API_CONSTANTS.API_BASE_URL);
    document.cookie = "BearerToken=" + connection.accessToken + "; domain= " + domain + "; path=/";

    // Start the connection
    try {
        await connection.start();
        console.warn("ConnectionID = ", connection.id);
    } catch (err) {
        console.log(err);
    }

    return connection;
};

export default { start, stop, setAccessToken };

